// Half-buried boulders: static circular obstacles. The player and enemies are pushed
// out of them (so they slide around); projectiles are consumed on contact. Placed at
// seeded positions (per run), clear of the spawn area and not clumping. Lookups go
// through a static spatial hash built once.

import { SpatialHash } from '../core/spatialHash';
import { Rng } from '../core/rng';
import { ALTAR, ARENA, BOULDER } from './balance';

export interface Boulder {
  x: number;
  y: number;
  radius: number;
}

export function generateBoulders(seed: number): Boulder[] {
  const rng = new Rng((seed ^ 0x9e3779b9) >>> 0);
  const out: Boulder[] = [];
  const cx = ARENA.width / 2;
  const cy = ARENA.height / 2;
  let attempts = 0;
  while (out.length < BOULDER.count && attempts < BOULDER.count * 25) {
    attempts++;
    const radius = rng.range(BOULDER.radiusMin, BOULDER.radiusMax);
    const x = rng.range(radius, ARENA.width - radius);
    const y = rng.range(radius, ARENA.height - radius);
    if (Math.hypot(x - cx, y - cy) < BOULDER.clearCenterPx) continue; // keep spawn clear
    if (Math.hypot(x - ALTAR.x, y - ALTAR.y) < ALTAR.radius + radius + 40) continue; // keep the altar clear
    let ok = true;
    for (const b of out) {
      if (Math.hypot(x - b.x, y - b.y) < b.radius + radius + BOULDER.minSpacingPx) {
        ok = false;
        break;
      }
    }
    if (ok) out.push({ x, y, radius });
  }
  return out;
}

export function buildBoulderHash(boulders: Boulder[], cellSize: number): SpatialHash {
  const hash = new SpatialHash(cellSize);
  for (let i = 0; i < boulders.length; i++) hash.insert(i, boulders[i].x, boulders[i].y);
  return hash;
}

/** Push a circle (player/enemy) out of any boulder it overlaps. Mutates obj.x/obj.y. */
export function pushOutOfBoulders(
  obj: { x: number; y: number },
  radius: number,
  boulders: Boulder[],
  hash: SpatialHash,
  buf: number[],
): void {
  const n = hash.query(obj.x, obj.y, radius + BOULDER.maxRadius, buf);
  for (let i = 0; i < n; i++) {
    const b = boulders[buf[i]];
    const dx = obj.x - b.x;
    const dy = obj.y - b.y;
    const minD = radius + b.radius;
    const d2 = dx * dx + dy * dy;
    if (d2 >= minD * minD) continue;
    if (d2 > 0.0001) {
      const d = Math.sqrt(d2);
      const push = (minD - d) / d;
      obj.x += dx * push;
      obj.y += dy * push;
    } else {
      obj.x += minD; // dead-center: shove out in a fixed direction
    }
  }
}

/** True if a circle overlaps any boulder (used to consume projectiles). */
export function hitsBoulder(
  x: number,
  y: number,
  radius: number,
  boulders: Boulder[],
  hash: SpatialHash,
  buf: number[],
): boolean {
  return firstBoulderHit(x, y, radius, boulders, hash, buf) !== null;
}

/** Return the blocking boulder, when present. Projectile weapons use the actual
 * obstacle position to reflect exactly once; other collision users only need the
 * boolean helper above. */
export function firstBoulderHit(
  x: number,
  y: number,
  radius: number,
  boulders: Boulder[],
  hash: SpatialHash,
  buf: number[],
): Boulder | null {
  const n = hash.query(x, y, radius + BOULDER.maxRadius, buf);
  for (let i = 0; i < n; i++) {
    const b = boulders[buf[i]];
    const dx = x - b.x;
    const dy = y - b.y;
    const minD = radius + b.radius;
    if (dx * dx + dy * dy <= minD * minD) return b;
  }
  return null;
}
